import { useSyncExternalStore } from 'react'

// Tiny event bridge between the walk screen / map (production code, DEV-guarded call sites) and the
// dev-only walk simulator. Holds no simulator code; safe to ship but only ever called under DEV.

export interface WalkSimSnapshot {
  turfId: string
  mode: 'walking' | 'driving'
  target: { key: string; lat: number; lng: number } | null
  segment: [number, number][] | null
  routeState: string
}

type LngLatObj = { lng: number; lat: number }

const snapshotListeners = new Set<(s: WalkSimSnapshot) => void>()
const clickListeners = new Set<(p: LngLatObj) => void>()
let lastSnapshot: WalkSimSnapshot | null = null

export function publishWalkSnapshot(s: WalkSimSnapshot): void {
  lastSnapshot = s
  for (const fn of [...snapshotListeners]) fn(s)
}

export function publishMapClick(lngLat: LngLatObj): void {
  for (const fn of [...clickListeners]) fn(lngLat)
}

/** Subscribes; the latest snapshot (if any) is replayed immediately. */
export function onWalkSnapshot(fn: (s: WalkSimSnapshot) => void): () => void {
  snapshotListeners.add(fn)
  if (lastSnapshot) fn(lastSnapshot)
  return () => { snapshotListeners.delete(fn) }
}

// Sim epoch: bumped by the simulator engine on activate/deactivate so DEV-only layout code can
// remount the current screen and re-subscribe its geolocation watch. Always 0 in production.
let epoch = 0
const epochListeners = new Set<() => void>()
export function bumpWalkSimEpoch(): void {
  epoch += 1
  for (const fn of [...epochListeners]) fn()
}
function subscribeEpoch(fn: () => void): () => void {
  epochListeners.add(fn)
  return () => { epochListeners.delete(fn) }
}
export function useWalkSimEpoch(): number {
  return useSyncExternalStore(subscribeEpoch, () => epoch, () => 0)
}

export function onMapClick(fn: (p: LngLatObj) => void): () => void {
  clickListeners.add(fn)
  return () => { clickListeners.delete(fn) }
}
